import React, { useEffect, useRef } from "react";
import { Box } from "@chakra-ui/react";

/**
 * A layer that scrolls with something else.
 *
 * The page around the paper is one long sheet with the paper: a note left
 * beside part (c) belongs beside part (c), so when the paper scrolls the
 * page scrolls with it. The paper is the only thing that really scrolls;
 * this follows it by the same distance, and is made tall enough to cover
 * everything the paper can scroll through.
 *
 * It also works the other way. A wheel turned over the page, where nothing
 * scrolls natively, is handed to the paper — otherwise scrolling beside the
 * paper would do nothing and the page would feel pinned. Anything marked
 * `data-own-scroll` (the question list) keeps its own wheel.
 *
 * Positions are written straight to the nodes rather than through state: a
 * scroll fires every frame, and re-rendering the paper that often would
 * make it stutter.
 *
 * `dotsRef`, if given, is the element carrying the dotted background, which
 * is moved too so the ground visibly travels with what is drawn on it.
 */
const ScrollLinkedBackdrop = ({ scrollRef, dotsRef, children }) => {
  const frameRef = useRef(null);
  const layerRef = useRef(null);

  useEffect(() => {
    const scroller = scrollRef?.current;
    const frame = frameRef.current;
    const layer = layerRef.current;
    if (!scroller || !frame || !layer) return undefined;
    const row = frame.parentElement;

    const sync = () => {
      const top = scroller.scrollTop;
      const reach = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
      layer.style.transform = `translateY(${-top}px)`;
      layer.style.height = `${frame.clientHeight + reach}px`;
      if (dotsRef?.current) dotsRef.current.style.backgroundPositionY = `${-top}px`;
    };

    const onWheel = (e) => {
      if (scroller.contains(e.target) || e.target.closest?.("[data-own-scroll]")) return;
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
      e.preventDefault();
      // Instant, not the paper's smooth jump-to-question: a wheel already
      // arrives as a stream of small steps.
      scroller.scrollBy({ top: e.deltaY, behavior: "instant" });
    };

    sync();
    scroller.addEventListener("scroll", sync, { passive: true });
    row?.addEventListener("wheel", onWheel, { passive: false });
    // The paper's height changes as parts open, answers grow and questions
    // change; the page has to keep covering all of it.
    const observer = new ResizeObserver(sync);
    observer.observe(scroller);
    observer.observe(frame);
    if (scroller.firstElementChild) observer.observe(scroller.firstElementChild);

    return () => {
      scroller.removeEventListener("scroll", sync);
      row?.removeEventListener("wheel", onWheel);
      observer.disconnect();
    };
  }, [scrollRef, dotsRef]);

  return (
    <Box ref={frameRef} position="absolute" inset="0" zIndex={0} overflow="hidden">
      <Box ref={layerRef} position="absolute" top="0" left="0" right="0" height="100%" willChange="transform">
        {children}
      </Box>
    </Box>
  );
};

export default ScrollLinkedBackdrop;
